// src/types/device.ts
var DEVICE_STATUS_VALUES = [
  "online",
  "chip_offline",
  "socket_offline",
  "offline",
  "unknown"
];
function isDeviceHealthy(status) {
  return status === "online";
}

// src/types/features.ts
var TOGGLEABLE_FEATURES = [
  "calls",
  "videophone",
  "openDoor",
  "camera",
  "contacts",
  "photos",
  "voicemail",
  "callHistory",
  "games",
  "agenda",
  "weather",
  "news",
  "info",
  /**
   * Domotics on the tablet (spec 20260826-domotica-via-resident-app).
   *
   * ⚠ Two things about this key are unlike every other one here.
   *
   * 1. It defaults to `false` (featureDefaults.ts). Behind it sit relays that are
   *    indistinguishable from lamps in the data: on the reference site 195 of 769
   *    outputs are `F_` — fire alarm, panic, and the door lock the openDoor gate
   *    exists to protect.
   * 2. It is SITE-OPT-IN (resolveFeatureChain.ts). A site must write an explicit
   *    `true`; silence closes it at every descendant. That is a third merge mode,
   *    distinct from both most-specific-wins and the sticky-OFF hard gate.
   */
  "domotics",
  /** Energie-pagina (spec 20261013-metering-afrekening). Uit en site-opt-in, zoals domotics. */
  "energy"
];

// src/types/knowledgeBase.ts
var KB_PAGE_STATUS = {
  fresh: {
    i18nKey: "KnowledgeBase.status_fresh",
    label: "Up to date",
    color: "success"
  },
  stale: {
    i18nKey: "KnowledgeBase.status_stale",
    label: "Needs review",
    color: "warning"
  },
  manual: {
    i18nKey: "KnowledgeBase.status_manual",
    label: "Manual",
    color: "info"
  },
  error: {
    i18nKey: "KnowledgeBase.status_error",
    label: "Error",
    color: "error"
  }
};
function resolveKbPageStatus(page) {
  if (!page) return "fresh";
  if (page.freshness) return page.freshness;
  if (page.lastError) return "error";
  if (page.source === "manual") return "manual";
  if (page.stale) return "stale";
  return "fresh";
}
function getKbPageStatusMeta(page) {
  const status = typeof page === "string" ? page : resolveKbPageStatus(page);
  return KB_PAGE_STATUS[status] || {
    i18nKey: "",
    label: (status || "").replace(/_/g, " "),
    color: "default"
  };
}

// src/types/alarm.ts
var BOARD_COMMENT_MIN_LENGTH = 10;

// src/types/permissionTemplate.ts
function resolveTemplate(globalRows, companyRows, slug) {
  const override = companyRows.find((r) => r.slug === slug);
  if (override) return override;
  return globalRows.find((r) => r.slug === slug);
}
function resolveTemplateList(globalRows, companyRows) {
  const slugs = /* @__PURE__ */ new Set([
    ...globalRows.map((r) => r.slug),
    ...companyRows.map((r) => r.slug)
  ]);
  const out = [];
  for (const slug of slugs) {
    const row = resolveTemplate(globalRows, companyRows, slug);
    if (row) out.push(row);
  }
  return out;
}
function templateScope(row) {
  return row.companyId === null || row.companyId === void 0 ? "global" : "company";
}
function applyTemplateToEnvelope(templatePermissions, granted = [], denied = []) {
  const deniedSet = new Set(denied);
  const nextDefault = templatePermissions.filter((k) => !deniedSet.has(k));
  const defaultSet = new Set(nextDefault);
  return {
    default: nextDefault,
    custom: [...new Set(granted)].filter(
      // A key already supplied by the template is not also an individual grant.
      // And a DENIED key must not come back through `custom` — that would let
      // contradictory stored state silently resurrect a revoked permission.
      // The two lists are mutually exclusive by construction (the matrix
      // reducer's grant path lifts the tombstone rather than adding to both),
      // so this only fires on malformed state, and on malformed state a
      // permission system fails closed.
      (k) => !defaultSet.has(k) && !deniedSet.has(k)
    ),
    denied: [...deniedSet]
  };
}
function driftReasonsFor(user, template) {
  const reasons = [];
  if ((user.templateVersion ?? 0) < template.version) {
    reasons.push("outdated-version");
  }
  const denied = new Set(user.permissions?.denied ?? []);
  const expected = template.permissions.filter((k) => !denied.has(k));
  const actual = user.permissions?.default ?? [];
  const sameLength = expected.length === actual.length;
  const sameMembers = sameLength && expected.every((k) => actual.includes(k));
  if (!sameMembers) reasons.push("hand-edited");
  return reasons;
}

// src/types/residentNotification.ts
var RESIDENT_NOTIFICATION_PRESENTATIONS = [
  "list",
  "dialog",
  "fullpage"
];
var RESIDENT_NOTIFICATION_PRIORITIES = [
  "low",
  "normal",
  "high",
  "urgent"
];
var RESIDENT_NOTIFICATION_DELIVERY_STATES = [
  "queued",
  "pushed",
  "displayed",
  "acknowledged",
  "actioned",
  "expired",
  "failed"
];
var RESIDENT_NOTIFICATION_RANKED_STATES = [
  "queued",
  "pushed",
  "displayed",
  "acknowledged",
  "actioned"
];
var RESIDENT_NOTIFICATION_WS_CHANNELS = [
  "none",
  "sent",
  "no_socket"
];
var RESIDENT_NOTIFICATION_MQTT_CHANNELS = [
  "none",
  "published",
  "unavailable",
  "error"
];
var RESIDENT_NOTIFICATION_VOICE_SOURCES = [
  "render",
  "native",
  "none"
];
var RESIDENT_NOTIFICATION_VOICE_REASONS = [
  "cache_miss",
  "play_failed",
  "muted_call",
  "preference_off",
  "no_audio"
];
var RESIDENT_NOTIFICATION_ACTION_TYPES = [
  "dismiss",
  "domotics_output",
  "automation_event",
  "navigate",
  "call"
];
var RESIDENT_NOTIFICATION_BACKGROUND_KINDS = ["image", "camera", "video"];
var RESIDENT_NOTIFICATION_NAVIGATE_ROUTES = [
  "/",
  "/agenda",
  "/apps",
  "/call-history",
  "/contacts",
  "/domotics",
  "/games",
  "/info",
  "/news",
  "/notifications",
  "/photos",
  "/settings",
  "/today",
  "/voicemail",
  "/weather"
];
function isAllowedNotificationRoute(route) {
  const allowed = RESIDENT_NOTIFICATION_NAVIGATE_ROUTES;
  return typeof route === "string" && allowed.indexOf(route) !== -1;
}
var RESIDENT_NOTIFICATION_FAILURE_REASONS = [
  "schema_missing",
  "no_targets",
  "no_location",
  "missing_title",
  "undelivered",
  "expired"
];
var RESIDENT_NOTIFICATION_COMMAND_TYPE = "resident_notification";
var RESIDENT_NOTIFICATION_CLEAR_COMMAND_TYPE = "resident_notification_clear";
var RESIDENT_NOTIFICATION_PAYLOAD_VERSION = 1;
var RESIDENT_NOTIFICATION_TRANSLATION_LANGUAGES = [
  "nl",
  "en",
  "fr",
  "de"
];
var RESIDENT_NOTIFICATION_TRANSLATABLE_FIELDS = [
  "title",
  "body",
  "ttsText",
  "actionLabels"
];
var RESIDENT_NOTIFICATION_SYSTEM_MESSAGE_KEYS = [
  "system_test"
];
function isForwardNotificationTransition(current, next) {
  const ladder = RESIDENT_NOTIFICATION_RANKED_STATES;
  const from = ladder.indexOf(current);
  const to = ladder.indexOf(next);
  if (from === -1 || to === -1) return false;
  return to > from;
}

// src/types/camera.ts
var CAMERA_GRANT_TTL_SECONDS = 180;

// src/types/metering.ts
var METERING_SELECTABLE_CHANNEL_TYPES = ["mbusdirect", "weblog", "modbustcp"];
var METERING_DEFAULT_POLL_SEC = {
  mbusdirect: 900,
  modbustcp: 60,
  weblog: 900
};
var METERING_QUALITIES = ["measured", "estimated", "suspect", "missing"];
var METERING_MEDIA = [
  "Heat",
  "Gas",
  "Water",
  "Electricity",
  "Cooling",
  "Unknown"
];
var METERING_READABLE_REGISTER_KEYS = [
  "energy_kWh",
  "volume_m3",
  "power_kW",
  "flow_m3h",
  "temp_flow_C",
  "temp_return_C"
];
var METERING_REGISTER_TYPES = ["uint16", "int16", "uint32be", "uint32sw", "float32be"];
var METERING_CHANNEL_NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,47}$/;
var METERING_UNIT_MEDIA = ["Heat", "Gas", "Water", "Electricity", "Cooling"];
var METERING_MBUS_BAUD_RATES = [300, 600, 1200, 2400, 4800, 9600, 19200, 38400];
var METERING_METER_DEVICE_TYPE = "energy_meter";

export { BOARD_COMMENT_MIN_LENGTH, CAMERA_GRANT_TTL_SECONDS, DEVICE_STATUS_VALUES, KB_PAGE_STATUS, METERING_CHANNEL_NAME_PATTERN, METERING_DEFAULT_POLL_SEC, METERING_MBUS_BAUD_RATES, METERING_MEDIA, METERING_METER_DEVICE_TYPE, METERING_QUALITIES, METERING_READABLE_REGISTER_KEYS, METERING_REGISTER_TYPES, METERING_SELECTABLE_CHANNEL_TYPES, METERING_UNIT_MEDIA, RESIDENT_NOTIFICATION_ACTION_TYPES, RESIDENT_NOTIFICATION_BACKGROUND_KINDS, RESIDENT_NOTIFICATION_CLEAR_COMMAND_TYPE, RESIDENT_NOTIFICATION_COMMAND_TYPE, RESIDENT_NOTIFICATION_DELIVERY_STATES, RESIDENT_NOTIFICATION_FAILURE_REASONS, RESIDENT_NOTIFICATION_MQTT_CHANNELS, RESIDENT_NOTIFICATION_NAVIGATE_ROUTES, RESIDENT_NOTIFICATION_PAYLOAD_VERSION, RESIDENT_NOTIFICATION_PRESENTATIONS, RESIDENT_NOTIFICATION_PRIORITIES, RESIDENT_NOTIFICATION_RANKED_STATES, RESIDENT_NOTIFICATION_SYSTEM_MESSAGE_KEYS, RESIDENT_NOTIFICATION_TRANSLATABLE_FIELDS, RESIDENT_NOTIFICATION_TRANSLATION_LANGUAGES, RESIDENT_NOTIFICATION_VOICE_REASONS, RESIDENT_NOTIFICATION_VOICE_SOURCES, RESIDENT_NOTIFICATION_WS_CHANNELS, TOGGLEABLE_FEATURES, applyTemplateToEnvelope, driftReasonsFor, getKbPageStatusMeta, isAllowedNotificationRoute, isDeviceHealthy, isForwardNotificationTransition, resolveKbPageStatus, resolveTemplate, resolveTemplateList, templateScope };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map